import { useMemo } from 'react';
import {
    Card,
    Table,
    Typography,
    Statistic,
    Row,
    Col,
    Select,
    Button,
    Space,
    Spin,
    Progress,
} from 'antd';
import { DatePicker } from '@/components/date_picker/date_picker';
import {
    BarChartOutlined,
    FileSearchOutlined,
    ReloadOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useBillingStatistics, useTenantBillingHistory } from '@/hooks/useSystemAdminApi';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { BillingHistoryItem, TenantBillingDetail } from 'breuer-supercharged-sdk';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function BillingStatistics() {
    const [searchParams, setSearchParams] = useSearchParams();

    // Get values from URL parameters
    const selectedTenant = searchParams.get('tenant') || '';
    const dateRange = useMemo(() => {
        const startParam = searchParams.get('startDate');
        const endParam = searchParams.get('endDate');

        if (startParam && endParam) {
            return [dayjs(startParam), dayjs(endParam)] as [dayjs.Dayjs, dayjs.Dayjs];
        }
        return null;
    }, [searchParams]);

    // Convert date range to ISO strings for the API
    const startDate = dateRange?.[0]?.toISOString();
    const endDate = dateRange?.[1]?.toISOString();

    // React Query hooks
    const { data: statistics, isLoading: loading, refetch: refetchStatistics } = useBillingStatistics(startDate, endDate);
    const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } = useTenantBillingHistory(
        selectedTenant || null,
        startDate,
        endDate
    );

    const history = historyData?.history || [];


    const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        const newParams = new URLSearchParams(searchParams);

        if (dates && dates[0] && dates[1]) {
            newParams.set('startDate', dates[0].format('YYYY-MM-DD'));
            newParams.set('endDate', dates[1].format('YYYY-MM-DD'));
        } else {
            newParams.delete('startDate');
            newParams.delete('endDate');
        }

        setSearchParams(newParams);
    };

    const handleRefresh = () => {
        refetchStatistics();
        if (selectedTenant) {
            refetchHistory();
        }
    };

    const tenantColumns: ColumnsType<TenantBillingDetail> = [
        {
            title: 'Mandant',
            key: 'tenant',
            render: (_, record) => (
                <Space>
                    <Text strong>{record.tenant.name}</Text>
                    {!record.tenant.isActive && <Text type="danger">(Inaktiv)</Text>}
                </Space>
            ),
        },
        {
            title: 'Gesamt Aktionen',
            key: 'totalActions',
            render: (_, record) => record.statistics.totalActions,
            sorter: (a, b) => a.statistics.totalActions - b.statistics.totalActions,
        },
        {
            title: 'PDF Generierungen',
            key: 'pdfGenerations',
            render: (_, record) => record.statistics.actionBreakdown.pdf_generation || 0,
        },
        {
            title: 'Anteil',
            key: 'percentage',
            render: (_, record) => {
                const percentage = statistics
                    ? Math.round((record.statistics.totalActions / statistics.totalActions) * 100)
                    : 0;
                return (
                    <Progress
                        percent={percentage}
                        size="small"
                        format={(percent) => `${percent}%`}
                    />
                );
            },
        },
    ];

    const historyColumns: ColumnsType<BillingHistoryItem> = [
        {
            title: 'Datum',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleString('de-DE'),
            sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Aktion',
            dataIndex: 'actionType',
            key: 'actionType',
            render: (type: string) => {
                const actionMap: Record<string, string> = {
                    pdf_generation: 'PDF Generierung',
                };
                return actionMap[type] || type;
            },
        },
        {
            title: 'Beschreibung',
            dataIndex: 'actionDescription',
            key: 'actionDescription',
        },
        {
            title: 'Metadaten',
            dataIndex: 'metadata',
            key: 'metadata',
            render: (metadata: string) => {
                if (!metadata) return '-';
                try {
                    const parsed = JSON.parse(metadata);
                    return <Text code>{JSON.stringify(parsed, null, 2)}</Text>;
                } catch {
                    return metadata;
                }
            },
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text>Abrechnungsstatistiken werden geladen...</Text>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}>Abrechnungsstatistik</Title>
                <Text type="secondary">
                    Übersicht über alle Abrechnungsaktivitäten im System
                </Text>
            </div>

            <Space style={{ marginBottom: 24 }}>
                <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    placeholder={['Startdatum', 'Enddatum']}
                    allowClear
                />
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                >
                    Aktualisieren
                </Button>
            </Space>

            {statistics && (
                <>
                    <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={8}>
                            <Card>
                                <Statistic
                                    title="Gesamt Mandanten"
                                    value={statistics.totalTenants}
                                    prefix={<TeamOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card>
                                <Statistic
                                    title="Gesamt Aktionen"
                                    value={statistics.totalActions}
                                    prefix={<BarChartOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card>
                                <Statistic
                                    title="PDF Generierungen"
                                    value={statistics.combinedActionBreakdown.pdf_generation || 0}
                                    prefix={<FileSearchOutlined />}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Card title="Mandanten-Übersicht" style={{ marginBottom: 24 }}>
                        <Table
                            columns={tenantColumns}
                            dataSource={statistics.tenantDetails}
                            rowKey={(record) => record.tenant.id}
                            pagination={false}
                            size="small"
                            scroll={{
                                x: true
                            }}
                        />
                    </Card>

                    <Card title="Abrechnungshistorie">
                        <div style={{ marginBottom: 16 }}>
                            <Select
                                placeholder="Mandant auswählen"
                                style={{ width: 300 }}
                                onChange={(value) => {
                                    const newParams = new URLSearchParams(searchParams);
                                    if (value) {
                                        newParams.set('tenant', value);
                                    } else {
                                        newParams.delete('tenant');
                                    }
                                    setSearchParams(newParams);
                                }}
                                value={selectedTenant || undefined}
                                allowClear
                            >
                                {statistics.tenantDetails.map((item) => (
                                    <Option key={item.tenant.id} value={item.tenant.id}>
                                        {item.tenant.name}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        {selectedTenant ? (
                            <Table
                                columns={historyColumns}
                                dataSource={history}
                                rowKey="id"
                                loading={historyLoading}
                                pagination={{ pageSize: 20 }}
                                size="small"
                                scroll={{ x: 'max-content' }}
                            />
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Text type="secondary">
                                    Wählen Sie einen Mandanten aus, um die Historie anzuzeigen
                                </Text>
                            </div>
                        )}
                    </Card>
                </>
            )}
        </div>
    );
}